import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { cn } from '../lib/class-names.js';
import { LoaderOverlay } from './LoaderOverlay.js';
/**
 * Brand button. Teal gradient primary, amber accent, glass secondary/outline. Touch screens get
 * 44px targets (`pointer-coarse:`); mouse UIs such as the admin portal stay compact.
 */
export const buttonVariants = cva('inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-semibold transition-[filter,transform,background-color,border-color,box-shadow] duration-150 select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none motion-reduce:active:scale-100', {
    variants: {
        variant: {
            default: 'bg-[image:var(--brand-teal-gradient)] text-white shadow-[0_8px_22px_-10px_var(--brand-glow-teal),inset_0_1px_0_rgb(255_255_255/0.18)] hover:bg-[image:var(--brand-teal-gradient-hover)]',
            amber: 'bg-[image:var(--brand-amber-gradient)] text-[var(--brand-amber-ink)] shadow-[0_8px_22px_-10px_var(--brand-glow-amber),inset_0_1px_0_rgb(255_255_255/0.4)] hover:brightness-105',
            secondary: 'border border-border bg-secondary text-secondary-foreground hover:border-input hover:bg-accent',
            outline: 'border border-input bg-transparent text-foreground hover:border-brand-amber hover:bg-accent',
            ghost: 'text-foreground hover:bg-accent',
        },
        size: {
            default: 'h-10 px-4 py-2 pointer-coarse:h-11',
            sm: 'h-8 rounded-lg px-3 text-xs pointer-coarse:h-11 pointer-coarse:px-4 pointer-coarse:text-sm',
            lg: 'h-12 px-8 text-base',
        },
    },
    defaultVariants: { variant: 'default', size: 'default' },
});
/** The small teal/amber ring shown inside a pending button (stops under reduced motion). */
export function ButtonSpinner() {
    return (_jsxs("svg", { "data-button-spinner": "", className: "size-4 shrink-0 animate-spin motion-reduce:animate-none", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", children: [_jsx("circle", { cx: "12", cy: "12", r: "9", fill: "none", stroke: "currentColor", strokeOpacity: "0.25", strokeWidth: "3" }), _jsx("path", { d: "M21 12a9 9 0 0 0-9-9", fill: "none", stroke: "currentColor", strokeWidth: "3", strokeLinecap: "round" })] }));
}
const isThenable = (value) => typeof value?.then === 'function';
/**
 * What a click handler returned decides the pending state: a promise keeps the button pending
 * until it settles (success or failure); anything else leaves it alone.
 */
export function trackClickResult(result, setRunning, isMounted) {
    if (!isThenable(result))
        return;
    setRunning(true);
    const done = () => {
        if (isMounted())
            setRunning(false);
    };
    result.then(done, done);
}
let clickScope = null;
/**
 * Called by an app's request layer (e.g. the react-query MutationCache `onMutate`) when a request
 * starts: if a Button click (or the form submit it triggered) started it, that button stays
 * pending until `work` settles. Returns whether a button took it.
 */
export function claimForCurrentClick(work) {
    if (!clickScope)
        return false;
    clickScope(work);
    return true;
}
/**
 * Opens the click scope around `run`. A submit button keeps it open until the end of the task,
 * because the browser fires the form's submit (and its request) after the click handler returns.
 */
export function withClickScope(claim, keepForSubmit, run) {
    const previous = clickScope;
    clickScope = claim;
    try {
        return run();
    }
    finally {
        if (keepForSubmit)
            setTimeout(() => {
                if (clickScope === claim)
                    clickScope = previous;
            }, 0);
        else
            clickScope = previous;
    }
}
/**
 * The button is pending while its async click handler runs (it returns a promise) or while a
 * request it started is in flight; a `pending` prop covers anything else.
 */
function useClickPending(onClick, isSubmit) {
    const [running, setRunning] = useState(0);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const handle = useCallback((event) => {
        const track = (work) => trackClickResult(work, (on) => setRunning((count) => Math.max(0, count + (on ? 1 : -1))), () => mounted.current);
        const result = withClickScope(track, isSubmit, () => onClick?.(event));
        track(result);
    }, [onClick, isSubmit]);
    return { running: running > 0, handle };
}
export function Button({ className, variant, size, asChild = false, pending = false, pendingOverlay, disabled, onClick, children, ...props }) {
    const { running, handle } = useClickPending(onClick, props.type === undefined || props.type === 'submit');
    if (asChild)
        return (_jsx(Slot, { className: cn(buttonVariants({ variant, size }), className), onClick: onClick, ...props, children: children }));
    const busy = pending || running;
    const button = (_jsxs("button", { className: cn(buttonVariants({ variant, size }), className), disabled: disabled === true || busy, "aria-busy": busy || undefined, onClick: handle, ...props, children: [busy ? _jsx(ButtonSpinner, {}) : null, children] }));
    // Always a fragment, so the button itself stays mounted (and focusable) when the overlay comes.
    return (_jsxs(_Fragment, { children: [button, busy && pendingOverlay ? _jsx(LoaderOverlay, { label: pendingOverlay }) : null] }));
}
//# sourceMappingURL=Button.js.map